import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import _ from 'lodash';
import { RootState } from 'store';

import FiltersBottomSheet, { FilterItem, PriceFilter, Select } from 'components/FiltersBottomSheet';
import FilterHotelRefundable from 'components/FiltersBottomSheet/components/FilterHotelRefundable';
import Loader from 'components/Loader';
import { Mobile, Desktop } from 'components/Responsive';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { HotelFilterResponse, HotelFilter } from 'models/hotelFilter';
import { TravelHotelContentLocationRequest } from 'models/hotelLocation';
import { HotelsResultRequestOrderCriteriaEnum } from 'models/hotelsSearch';
import { amplitudelogEvent, FilterGroupEvent, SelectFilterEvent } from 'services/amplitudeService';
import { HotelAmenities } from 'services/hotelService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

import LoaderDesktop from './components/LoaderDesktop';
import Name from './components/Name';
import Order from './components/Order';
import Stars from './components/Stars';

interface Props {
  show: boolean;
  currency: string;
  defaultFilters?: HotelFilter;
  availableFilters?: HotelFilterResponse;
  amenitiesCodes: HotelAmenities[];
  onSearch: (filters: HotelFilter) => void;
  onClose: () => void;
  destinationPoint?: TravelHotelContentLocationRequest;
  checkInDate?: Date;
  checkOutDate?: Date;
  isLoading: boolean;
}

const DEFAULT_ORDER = HotelsResultRequestOrderCriteriaEnum.RECOMENDATION;
const TIMEOUT_INTERVAL = 700;

export const HotelsFilters = ({
  defaultFilters,
  availableFilters,
  amenitiesCodes,
  currency,
  onSearch,
  onClose,
  destinationPoint,
  checkInDate,
  checkOutDate,
  isLoading,
  ...props
}: Props) => {
  const isDesktop = useIsDesktop();
  const { t } = useStoreTranslation();
  const [searchTimeout, setSearchTimeout] = useState<number>();

  const [orderCriteria, setOrderCriteria] = useState(defaultFilters?.orderCriteria || DEFAULT_ORDER);
  const [categories, setCategories] = useState<string[]>(defaultFilters?.categories || []);
  const [price, setPrice] = useState<number[]>(
    defaultFilters?.priceFrom && defaultFilters.priceTo ? [defaultFilters?.priceFrom, defaultFilters.priceTo] : [],
  );
  const [hotelName, setHotelName] = useState(defaultFilters?.hotelName || '');
  const [zones, setZones] = useState<string[]>(defaultFilters?.zones || []);
  const [amenities, setAmenities] = useState<string[]>(defaultFilters?.ammenities || []);
  const [refundableType, setRefundableType] = useState<string | undefined>(defaultFilters?.refundableType);

  const [minMax, setMinMax] = useState<number[]>(
    defaultFilters?.priceFrom && defaultFilters.priceTo ? [defaultFilters?.priceFrom, defaultFilters.priceTo] : [],
  );
  const [filtersCount, setFiltersCount] = useState(0);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (availableFilters && (!defaultFilters?.priceFrom || !defaultFilters?.priceTo)) {
      setPrice([availableFilters.minPrice, availableFilters.maxPrice]);

      if (availableFilters.minPrice && availableFilters.maxPrice) {
        setMinMax([availableFilters.minPrice, availableFilters.maxPrice]);
      }
    }
  }, [availableFilters, defaultFilters]);

  const search = useSelector((state: RootState) => state.hotels.search);
  const user = useSelector((state: RootState) => state.user.accountResult);

  const sendAmplitudeEvent = (filterGroup: FilterGroupEvent, filterValue: any) => {
    const amplitudeSelectFilter: SelectFilterEvent = {
      FILTER_GROUP: filterGroup,
      FILTER_VALUE: filterValue.toString(),
      TRAVEL_PRODUCT_TYPE: 'HOTELS',
      TRAVEL_FROM: '',
      TRAVEL_TO: destinationPoint?.id.toString() ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkInDate).toString(),
      TRAVEL_DATE_FROM: checkInDate?.toString() ?? '',
      TRAVEL_DATE_TO: checkOutDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(checkInDate, checkOutDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: '',
      TRAVEL_ROOMS: '1',
      TRAVEL_WITH_BAGGAGE: '',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: search?.searchId ?? '',
    };

    amplitudelogEvent(amplitudeEvents.SELECT_FILTER, amplitudeSelectFilter, user);
  };

  useEffect(() => {
    let count = 0;
    if (orderCriteria !== DEFAULT_ORDER) {
      count++;
    }

    if (categories && categories.length > 0) {
      count += _.uniq(categories).length;
    }

    if (price && minMax && (price.first() !== minMax.first() || price.last() !== minMax.last())) {
      count++;
    }

    if (hotelName && hotelName.length > 0) {
      count++;
    }

    if (zones && zones.length > 0) {
      count++;
    }

    if (amenities && amenities.length > 0) {
      count++;
    }

    if (refundableType) {
      count++;
    }

    setFiltersCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenities, categories, hotelName, minMax, refundableType, orderCriteria, price, zones]);

  const handleSearch = useCallback(() => {
    const filter: HotelFilter = {};
    if (orderCriteria) filter.orderCriteria = orderCriteria;
    if (categories.length > 0) filter.categories = categories;
    filter.hotelName = hotelName;
    if (price.first() !== minMax.first() || price.last() !== minMax.last()) {
      filter.priceFrom = price.first();
      filter.priceTo = price.last();
    }
    if (zones) filter.zones = zones;
    if (amenities) filter.ammenities = amenities;
    if (refundableType) filter.refundableType = refundableType;

    onSearch(filter);
  }, [amenities, categories, hotelName, minMax, onSearch, orderCriteria, price, refundableType, zones]);

  const getTranslatedRefundableOption = (text: string) => {
    if (text === 'Refundable') return t('hotelsFilters.cancelPolicy.refundable');
    if (text === 'NonRefundable') return t('hotelsFilters.cancelPolicy.nonRefundable');
    return text;
  };

  const handleSearchName = (value: string) => {
    setHotelName(value);
    let timeoutId = searchTimeout;
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      if (isDesktop) setChanged(true);
      sendAmplitudeEvent(FilterGroupEvent.HOTEL_NAME, value);
    }, TIMEOUT_INTERVAL);
    setSearchTimeout(timeoutId);
  };

  const handleSearchPrice = (values: number[]) => {
    setPrice(values);
    let timeoutId = searchTimeout;
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      if (isDesktop) setChanged(true);
      sendAmplitudeEvent(FilterGroupEvent.PRICE, values);
    }, TIMEOUT_INTERVAL);
    setSearchTimeout(timeoutId);
  };

  useEffect(() => {
    if (isDesktop && changed) {
      handleSearch();
      setChanged(false);
    }
  }, [changed, handleSearch, isDesktop]);

  const handleRefundableImageEvent = () => {
    if (refundableType === undefined) {
      setRefundableType('Refundable');
      if (isDesktop) setChanged(true);
      return;
    }

    if (refundableType.toLowerCase() === 'refundable') {
      setRefundableType(undefined);
    } else {
      setRefundableType('Refundable');
    }

    if (isDesktop) setChanged(true);
    return;
  };

  const renderContent = (filters: HotelFilterResponse) => (
    <>
      <FilterHotelRefundable
        refundableType={refundableType}
        onChange={handleRefundableImageEvent}
      ></FilterHotelRefundable>
      <FilterItem
        name={t('hotelsFilters.hotelName.title')}
        changed={hotelName && hotelName.length > 0 ? true : false}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Name value={hotelName} onChange={handleSearchName} />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.order.title')}
        changed={orderCriteria !== DEFAULT_ORDER}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Order
          value={orderCriteria}
          onChange={(value) => {
            if (orderCriteria !== value) sendAmplitudeEvent(FilterGroupEvent.ORDER, value);
            setOrderCriteria(value);
            if (isDesktop) setChanged(true);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.price.title')}
        changed={price.first() !== minMax.first() || price.last() !== minMax.last()}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <PriceFilter
          min={filters.minPrice}
          max={filters.maxPrice}
          value={price}
          currency={currency}
          onChange={handleSearchPrice}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.cancelPolicy.title')}
        changed={refundableType ? true : false}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            availableFilters?.nonRefundable.map((item) => ({
              ...item,
              label: getTranslatedRefundableOption(item.text),
            })) || []
          }
          values={refundableType ? [refundableType] : []}
          onChange={(value: string[]) => {
            setRefundableType(value[0]);
            if (isDesktop) setChanged(true);
          }}
          allowSelectOne
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.HOTEL_POLICIES, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.stars.title')}
        count={_.uniq(categories).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Stars
          categories={filters.category}
          values={categories}
          onChange={(values) => {
            setCategories(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.STARS, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.zone.title')}
        count={_.uniq(zones).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={availableFilters?.zones.map((zone) => ({ text: zone.Text, count: zone.Count })) || []}
          values={zones}
          onChange={(values) => {
            setZones(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.LOCATION, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('hotelsFilters.amenities.title')}
        count={_.uniq(amenities).length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={(availableFilters?.ammenities || [])
            .map((amenity) => ({
              ...amenity,
              label: amenitiesCodes.find((ac) => ac.code === amenity.text)?.name || '',
            }))
            .filter((amenity) => amenity.label)}
          values={amenities}
          onChange={(values) => {
            setAmenities(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked)
              sendAmplitudeEvent(FilterGroupEvent.AMENITIES, amenitiesCodes.find((ac) => ac.code === value)?.name);
          }}
        />
      </FilterItem>
    </>
  );

  const handleClearFilters = useCallback(() => {
    setOrderCriteria(DEFAULT_ORDER);
    setCategories([]);
    setPrice(minMax);
    setHotelName('');
    setZones([]);
    setAmenities([]);
    setRefundableType(undefined);
  }, [minMax]);

  useEffect(() => {
    if (typeof defaultFilters === 'undefined') {
      handleClearFilters();
      return;
    }
    setOrderCriteria(defaultFilters?.orderCriteria ? defaultFilters.orderCriteria : DEFAULT_ORDER);
    setCategories(defaultFilters?.categories ? defaultFilters.categories : []);
    setPrice(
      defaultFilters?.priceFrom && defaultFilters.priceTo
        ? [defaultFilters?.priceFrom, defaultFilters.priceTo]
        : minMax,
    );
    setHotelName(defaultFilters?.hotelName ? defaultFilters.hotelName : '');
    setZones(defaultFilters?.zones ? defaultFilters.zones : []);
    setAmenities(defaultFilters?.ammenities ? defaultFilters.ammenities : []);
    setRefundableType(defaultFilters?.refundableType ? defaultFilters.refundableType : '');
  }, [defaultFilters, handleClearFilters, minMax]);

  return (
    <>
      <Mobile>
        <FiltersBottomSheet
          {...props}
          showSearchButton={Boolean(availableFilters)}
          onClose={onClose}
          onSearch={handleSearch}
          filtersCount={filtersCount}
          onClearFilter={handleClearFilters}
        >
          {!availableFilters ? <Loader fullScreen /> : renderContent(availableFilters)}
        </FiltersBottomSheet>
      </Mobile>
      <Desktop>{isLoading ? <LoaderDesktop /> : availableFilters && renderContent(availableFilters)}</Desktop>
    </>
  );
};

export default HotelsFilters;
